<template>
    <div>
        <div class="content">

            <div class="container-fluid mt-4">

                <stats-card title="Orchard details" type="default" :sub-title="groupData.name"  :isBiosecurity="groupData.surveillance" icon="fas fa-tree">

                    <template slot="actions">
                        <a class="dropdown-item" :href="`/#/farms/${groupData.farmId}`">View parent farm</a>
                        <a class="dropdown-item" v-if="groupData.status != 'deleted'" @click="showDeleteModal = true" href="javscript:void(0)">Delete Orchard</a>
                        <a class="dropdown-item" v-if="groupData.status != 'active'" @click="activateGroup" href="javascript:void(0)">Activate Orchard</a>
                    </template>

                    <template slot="append">

                        <base-alert type="danger" v-if="groupData.status =='deleted'">
                            This orchard is currently <strong>deleted</strong>
                        </base-alert>

                        <b-card no-bod class="tab-wrapper">

                            <b-card-header header-tag="nav">
                                <b-nav card-header tabs>
                                    <b-nav-item @click="setActiveTab(2)" :active="activeTab == 2">
                                        Orchard Details
                                        <span v-if="healthIssues.length > 0" :class="`ml-1 badge badge-warning`">{{healthIssues.length}}</span>
                                    </b-nav-item>
                                    <b-nav-item @click="setActiveTab(1)" :active="activeTab == 1">
                                        <i class="fas fa-map-marker-alt"></i>
                                        Sensors
                                    </b-nav-item>
                                    <b-nav-item @click="setActiveTab(3)" :active="activeTab == 3">
                                        <i class="fas fa-user"></i>
                                        Users
                                    </b-nav-item>
                                    <b-nav-item @click="setActiveTab(4)"  :active="activeTab == 4">
                                        <i class="fas fa-bug"></i>
                                        Detections
                                    </b-nav-item>
                                </b-nav>
                            </b-card-header>

                            <div class="tab-container">

                                <div class="mt-3" v-if="activeTab == 1">
                                    <entity-picker
                                        key="groupSensorsPicker"
                                        @onSelect="handleTrapUpdate"
                                        placeHolder="Search to add a trap"
                                        apiEndpoint="getSensors"
                                        :multiple="true"
                                        :selected.sync="groupSensors"
                                        :canBeEmpty="true"
                                        rootProp="sensors"
                                        :canEdit="true"
                                        :tableOptions="tableOptions"
                                        editPath="/sensors/"
                                        :tableColumns="groupTableColumns"
                                        :cursorData="groupSensorsCursor"
                                    >
                                    </entity-picker>
                                </div>

                                <div class="mt-3" v-if="activeTab == 3">
                                    <entity-picker
                                        key="groupUsersPicker"
                                        @onSelect="handleTrapUpdate"
                                        placeHolder="Search users"
                                        apiEndpoint="getUsers"
                                        :canRefresh="false"
                                        :selected="groupDataUsers"
                                        :canSearch="false"
                                        rootProp="users"
                                        :tableColumns="userTableColumns"
                                        :canEdit="true"
                                        :canSelect="false"
                                        editPath="/users/"
                                    >
                                    </entity-picker>
                                </div>

                                <div class="mt-3" v-if="activeTab == 2">

                                    <base-alert type="warning" v-if="healthIssues.length > 0">
                                        Please check this orchard's missing properties:
                                        <ul>
                                            <li v-for="issue in healthIssues" :key="issue">{{issue}}</li>
                                        </ul>
                                    </base-alert>

                                    <base-alert>
                                        This orchard was created on {{groupData.dateCreated}}
                                    </base-alert>

                                    <form-group :group="updatedGroupData"></form-group>

                                    <label class="form-control-label">Location:</label>

                                    <div v-if="!groupData.location.isValid" class="mt-1">
                                        <base-alert type="info">
                                            This orchard has no location yet. One will automatically be set once traps are assigned, but you can set a temporary one in the meantime.
                                        </base-alert>

                                        <set-location key="addressLookup" @onLocationSet="setLocation"></set-location>
                                    </div>

                                    <set-location v-if="groupData.location.isValid" key="locationLookup" :showInput="false" :location="groupData.location" @onLocationSet="setLocation"></set-location>

                                    <div class="mt-3" align="right">
                                        <base-button type="primary" @click="updateGroup">
                                            Update details
                                        </base-button>
                                    </div>
                                </div>

                                <div class="mt-3" v-if="activeTab == 4">
                                    <el-date-picker
                                        v-model="pickedDate"
                                        type="daterange"
                                        format="dd/MM/yyyy"
                                        range-separator="To"
                                        start-placeholder="Start date"
                                        end-placeholder="End date"
                                        :picker-options="pickerOptions"
                                        value-format="timestamp"
                                        @change="onDatePick"
                                        >
                                    </el-date-picker>

                                    <entity-picker
                                        v-if="groupDetections.length > 0"
                                        key="groupDetections"
                                        placeHolder="Search for a user to add or remove"
                                        apiEndpoint="getGroupDetections"
                                        :multiple="true"
                                        :selected="groupDetections"
                                        :canRefresh="false"
                                        :canBeEmpty="true"
                                        :canSearch="false"
                                        :canSelect="false"
                                        :tableColumns="detectionTableColumns"
                                        rootProp="detections"
                                    >
                                    </entity-picker>

                                    <base-alert v-if="detections.length"  class="mt-4">
                                        Detection charts / data table will go here
                                    </base-alert>
                                </div>

                            </div>
                        </b-card>

                    </template>

                </stats-card>
            </div>
        </div>

        <modal :show.sync="showDeleteModal">
            <h6 slot="header" class="modal-title">Delete Orchard</h6>

            <base-alert type="danger">
                Are you sure you want to <strong>delete</strong> this orchard?
            </base-alert>

            <template slot="footer">
                <base-button type="blank" @click="showDeleteModal = false">Cancel</base-button>
                <base-button type="danger" @click="deleteGroup">Yes, delete</base-button>
            </template>
        </modal>
    </div>

</template>

<script>
import api from '@/api'
import { GroupModel } from '@/models/group/group'
import { tableOptions, groupTableColumns } from '@/config/SensorTable'
import { tableColumns, detectionTableOptions } from '@/config/DetectionTable'
import { userTableColumns } from '@/config/GroupTable'
import { getEntityIssues } from '@/util/EntityHealth'
import { DatePicker } from 'element-ui';
import { TimeHelper } from '@/helpers/TimeHelper';
import { timezones, datePickerReadyDates } from '@/config/Time'
import { StringHelper } from '../helpers/StringHelper'

export default {
  components: {
      [DatePicker.name]: DatePicker
  },
  data() {
    return {
      pickedDate: '',
      activeTab: 2,
      showDeleteModal: false,
      timezones: timezones,
      healthItems: [
          'timezone',
          'address',
          'location',
          'produce',
          'description',
          'phone',
          'email'
      ],
      pickerOptions: datePickerReadyDates,
      detections: [],
      healthIssues: [],
      groupData: null,
      groupSensors: [],
      originalGroupSensors: [],
      groupDataUsers: [],
      groupDetections: [],
      updatedGroupData: null,
      detectionTableColumns: tableColumns,
      groupTableColumns: groupTableColumns,
      userTableColumns: userTableColumns,
      detectionTableOptions: detectionTableOptions,
      tableOptions: tableOptions,
      groupSensorsCursor: null,
      detectionDateRange: `start:${TimeHelper.timestampFromDaysAgo(7)}`
    };
  },
  created: function() {
    this.fetchGroup()
  },
  methods: {
    fetchGroup: async function() {
        this.$emit('loading', true);
        const results = await api.getGroup(this.$route.params.id)
        this.$emit('loading', false);
        this.groupData = new GroupModel(results)
        this.updatedGroupData = new GroupModel(results)
        this.checkEntityHealth();
    },
    onDatePick: async function(range) {
        this.detectionDateRange = `start:${TimeHelper.toSeconds(range[0])},end:${TimeHelper.toSeconds(range[1])}`
        this.fetchGroupDetections()
    },
    setLocation: function(location) {
        this.updatedGroupData.location.populate(location)
    },
    setActiveTab: async function(tab) {

        switch(tab) {
            case 1:
                this.$emit('loading', true);
                const gSensors = await api.getGroupSensors(this.groupData.id)
                this.$emit('loading', false);
                this.groupSensors = gSensors.sensors;
                this.groupSensorsCursor = gSensors.cursor
                this.originalGroupSensors = JSON.parse(JSON.stringify(gSensors.sensors))
                break;
            case 3:
                if(this.groupDataUsers.length == 0) {
                    this.$emit('loading', true);
                    const gUsers = await api.getGroupUsers(this.groupData.id)
                    this.$emit('loading', false);
                    this.groupDataUsers = gUsers.users
                }
                break;
            case 4:
                if(this.groupDetections.length == 0) {
                    this.fetchGroupDetections();
                }
                break;
        }

        this.activeTab = tab
    },
    fetchGroupDetections: async function() {
        this.groupDetections = [];
        this.$emit('loading', true);
        const gDetections = await api.getGroupDetections(this.groupData.id,{
            filter: this.detectionDateRange
        })
        this.$emit('loading', false);
        this.groupDetections = gDetections.detections
    },
    handleTrapUpdate: async function(sensors) {
        let originalGroupSensorsIds = this.originalGroupSensors.map((o) => o.id)
        let updatedGroupSensorsIds = sensors.map((g) => g.id)
        let addedSensors = sensors.filter((g) => !originalGroupSensorsIds.includes(g.id))
        let removedSensors = this.originalGroupSensors.filter((g) => !updatedGroupSensorsIds.includes(g.id) && g.sensorRemovedOn === null)
        let reAddedSensors =  this.originalGroupSensors.filter((g) => !updatedGroupSensorsIds.includes(g.id) && g.sensorRemovedOn !== null)

        if(addedSensors.length > 0 || removedSensors.length > 0 || reAddedSensors.length > 0) {
            this.$emit('loading', true);

            if(addedSensors.length > 0) {
                let groupUpdate = await api.addSensorsToGroup(this.groupData.id,addedSensors.map(function(s) {
                    return {
                        sensorId: s.id
                    }
                }))
                this.$emit('onApiResult', `${addedSensors.length} trap(s) added to orchard`);
            }

            if(reAddedSensors.length > 0) {
                let groupUpdate = await api.addSensorsToGroup(this.groupData.id,reAddedSensors.map(function(s) {
                    return {
                        sensorId: s.id
                    }
                }))
                this.$emit('onApiResult', `${reAddedSensors.length} trap(s) re-added to orchard`);
            }

            if(removedSensors.length > 0) {
                let groupUpdate = await api.removeSensorsFromGroup(this.groupData.id,removedSensors.map(function(s) {
                    return {
                        sensorId: s.id
                    }
                }))
                this.$emit('onApiResult', `${removedSensors.length} trap(s) removed from orchard`);
            }

            this.originalGroupSensors = []
            this.groupSensors = []
            this.$emit('loading', false);
            this.setActiveTab(1);
        }
        window.location.reload();
    },
    checkEntityHealth: function() {
        this.healthIssues = getEntityIssues(this.healthItems,this.groupData)
    },
    updateGroup: async function() {
      this.updatedGroupData = StringHelper.trimObjectStrings(this.updatedGroupData);
      this.$emit('loading', true);
      const results = await api.updateGroup(this.groupData.id,this.updatedGroupData.toDetailsUpdateJson(this.groupData));
      this.$emit('onApiResult', "Orchard has been updated successfully");
      this.fetchGroup()
    },
    activateGroup: async function() {
        this.updatedGroupData.status = 'active'
        this.updateGroup()
    },
    deleteGroup: async function() {
        this.showDeleteModal = false
        this.updatedGroupData.status = "deleted"
        this.updateGroup()
    }

  }
};
</script>
